import { useState, useRef, type DragEvent, type ChangeEvent } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Upload, FileText, ArrowLeft, Loader2, X } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Props extends Omit<AppShellProps, 'children'> {
  event: { id: number; name: string }
  urls: AppShellProps['urls'] & {
    import: string
    eventUsers: string
  }
  translations: AppShellProps['translations'] & {
    importFromCsv: string
    uploadCsvFile: string
    import: string
    selectFile: string
    back: string
    dragDrop: string
  }
}

const SUPPORTED_FILE_EXTENSIONS = ['.csv', '.xlsx', '.xls']
const SUPPORTED_FILE_ACCEPT = [
  '.csv',
  '.xlsx',
  '.xls',
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].join(',')

function isSupportedImportFile(file: File) {
  const fileName = file.name.toLowerCase()
  return SUPPORTED_FILE_EXTENSIONS.some((extension) => fileName.endsWith(extension))
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function NewImport(props: Props) {
  const { event, urls, translations: t, ...shell } = props
  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [dragging, setDragging] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  function handleDrop(e: DragEvent) {
    e.preventDefault()
    setDragging(false)
    const f = e.dataTransfer.files?.[0]
    if (f && isSupportedImportFile(f)) setFile(f)
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null
    setFile(f && isSupportedImportFile(f) ? f : null)
  }

  function handleSubmit() {
    if (!file) return
    setSubmitting(true)
    const formData = new FormData()
    formData.append('import_file', file)
    router.post(urls.import, formData as any, {
      forceFormData: true,
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={`${t.importFromCsv} — ${event.name}`} />

      <div className="mx-auto max-w-xl py-10 px-4">
        <Button
          variant="ghost"
          size="sm"
          className="mb-6 text-[#91918E]"
          onClick={() => router.visit(urls.eventUsers)}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t.back}
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-[#37352F]">{t.importFromCsv}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              className={`
                flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed
                px-6 py-12 transition-colors
                ${dragging ? 'border-[#2383E2] bg-blue-50' : 'border-[#E8E8E5] hover:border-[#91918E]'}
              `}
            >
              {file ? (
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8 text-[#2383E2]" />
                  <div>
                    <p className="text-sm font-medium text-[#37352F]">{file.name}</p>
                    <p className="text-xs text-[#91918E]">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setFile(null) }}
                    className="rounded p-1 hover:bg-[#F1F1EF]"
                  >
                    <X className="h-4 w-4 text-[#91918E]" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="mb-3 h-10 w-10 text-[#91918E]" />
                  <p className="text-sm font-medium text-[#37352F]">{t.selectFile}</p>
                  <p className="mt-1 text-xs text-[#91918E]">{t.dragDrop}</p>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                accept={SUPPORTED_FILE_ACCEPT}
                className="hidden"
                onChange={handleChange}
              />
            </div>

            {/* Submit */}
            <Button
              className="w-full bg-[#2383E2] hover:bg-[#1b6ec2]"
              disabled={!file || submitting}
              onClick={handleSubmit}
            >
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              {t.import}
            </Button>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  )
}
